// @flow

// Libs
import React, { Component } from 'react'

// Utils
import traverse from 'fc-utils/traverse'

// Components
import FormField from '@react-mdc/form-field'
import Button from '@react-mdc/button'
import Select from 'react-select'
import { Errors } from 'hfty-components-errors'

// Constants
import { PROP_ID, PROP_ASSIGNED_TO, PROP_STATUS, PROP_FIELD, PROP_SUBFIELD } from 'hfty-model-soil-jobs'
import { SHIPPING, RESULTS_POSTED } from 'fc-model-sampling-jobs'
import {
  MSG_REOPEN_JOB_SIDEBAR_HEADING,
  MSG_REOPEN_JOB_SIDEBAR_MESSAGE,
  MSG_REOPEN_JOB_SIDEBAR_REOPEN_BTN,
  MSG_REOPEN_JOB_SIDEBAR_CANCEL_BTN,
} from '../constants'

// Messages
import { FormattedMessage } from 'react-intl'
import messages from '../messages'

// Styles
import { sidebar as sidebarCommonStyle, INDENT, message as messageCommonStyle } from 'hfty-styles/src/common'

const sidebar = sidebarCommonStyle
const styles = {
  bottomIndent: {
    marginBottom: INDENT,
  },
}

// Getters
const getStatus = traverse(['operation', 'model', 'job', PROP_STATUS], { default: '' })
const getAssignedTo = traverse(['operation', 'model', 'job', PROP_ASSIGNED_TO], { default: '' })
const getField = function (props: Object): number {
  const fieldId = traverse(['operation', 'model', 'job', PROP_FIELD], { default: '' })(props)

  return props.fieldSelectorOptions.find((fieldOption: Object): boolean => fieldOption.value === fieldId)
}
const getStatusOption = (props: Object): Object => {
  const status = getStatus(props)

  return {
    value: status,
    label: status,
  }
}

// Types
type Props = {
  operation: Object,
  subfields: Array,
  fieldSelectorOptions: Array,
  userSelectorOptions: Array,
  jobStatusOptions: Array,
  onChange: Function,
  onSubmit: Function,
  onCancel: Function,
}

export default class ReopenJob extends Component {
  props: Props
  static defaultProps = {
    operation: {},
    subfields: [],
    fieldSelectorOptions: [],
    userSelectorOptions: [],
    jobStatusOptions: [],
  }

  constructor(props: Props) {
    super(props)

    this.handleFieldChange = this.handleFieldChange.bind(this)
    this.handleAssigneeChange = this.handleAssigneeChange.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleCancel = this.handleCancel.bind(this)
  }

  handleFieldChange(option: Object) {
    const fieldId = option ? option.value : ''
    const subfield = this.props.subfields.find((sf: Object): boolean => sf.parent === fieldId)

    if (fieldId && subfield) {
      this.handleChange({
        [PROP_FIELD]: fieldId,
        [PROP_SUBFIELD]: subfield.id,
      })
    }
  }

  handleAssigneeChange(option: Object) {
    const optionValue = option ? option.value : ''

    this.handleChange({ [PROP_ASSIGNED_TO]: optionValue })
  }

  handleChange(changedModelProps: Object) {
    const jobModel = traverse(['operation', 'model', 'job'], { default: '' })(this.props)

    this.props.onChange({
      ...this.props.operation,
      model: {
        job: {
          ...jobModel,
          ...changedModelProps,
        },
      },
    })
  }

  handleSubmit(e: Event) {
    e.preventDefault()

    this.props.onSubmit([this.props.operation])
  }

  handleCancel(e: Event) {
    e.preventDefault()

    this.props.onCancel([this.props.operation])
  }

  render(): React.Element<any> {
    const { operation } = this.props
    const hasErrors = operation && !!operation.errors.length
    const disableEdit = getStatus(this.props) === SHIPPING || getStatus(this.props) === RESULTS_POSTED
    const jobId = traverse(['model', 'job', PROP_ID], { default: '' })(operation)
    const PREFIX = 'reopen-job'

    return (
      <div style={sidebar.container}>
        <h3 style={sidebar.h3}>
          <FormattedMessage {...messages[MSG_REOPEN_JOB_SIDEBAR_HEADING]} /> #{jobId}
        </h3>
        {hasErrors && <Errors errors={operation.errors} style={styles.bottomIndent} />}
        <form style={sidebar.form}>
          <p style={messageCommonStyle}>
            <FormattedMessage {...messages[MSG_REOPEN_JOB_SIDEBAR_MESSAGE]} />
          </p>
          <div className="select-group-aligned-right w68">
            <FormField key={`${PREFIX}-field`} style={styles.bottomIndent}>
              <label htmlFor={`${PREFIX}-field-select`}>Field</label>
              <Select
                name={`${PREFIX}-field-select`}
                value={getField(this.props)}
                options={this.props.fieldSelectorOptions}
                className="clear-margin"
                onChange={this.handleFieldChange}
                disabled
              />
            </FormField>
            <FormField key={`${PREFIX}-assigned-to`} style={styles.bottomIndent}>
              <label htmlFor={`${PREFIX}-assigned-to`}>Assigned To</label>
              <Select
                name={`${PREFIX}-assigned-to`}
                value={getAssignedTo(this.props)}
                options={this.props.userSelectorOptions}
                className="clear-margin"
                onChange={this.handleAssigneeChange}
                disabled={disableEdit}
              />
            </FormField>
            <FormField key={`${PREFIX}-status`} style={styles.bottomIndent}>
              <label htmlFor={`${PREFIX}-status`}>Status</label>
              <Select
                name={`${PREFIX}-status`}
                value={getStatusOption(this.props)}
                options={this.props.jobStatusOptions}
                className="clear-margin"
                disabled
              />
            </FormField>
          </div>
          <div style={sidebar.buttonWrapper}>
            <Button raised primary disabled={disableEdit} style={sidebar.button} onClick={this.handleSubmit}>
              <FormattedMessage {...messages[MSG_REOPEN_JOB_SIDEBAR_REOPEN_BTN]} />
            </Button>
            <Button raised disabled={disableEdit} onClick={this.handleCancel}>
              <FormattedMessage {...messages[MSG_REOPEN_JOB_SIDEBAR_CANCEL_BTN]} />
            </Button>
          </div>
        </form>
      </div>
    )
  }
}
